import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { apiClient } from '../services/apiClient'
import { useAuth } from '../hooks/useAuth'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshUser, user } = useAuth()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'invalid'>('verifying')
  const [message, setMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const hasStartedVerification = useRef(false)

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      setStatus('invalid')
      setMessage('Invalid verification link. Please check your email for the correct link.')
      return
    }

    // Prevent multiple verification attempts
    if (hasStartedVerification.current) {
      return
    }

    const verifyEmail = async () => {
      hasStartedVerification.current = true
      
      try {
        console.log('🔍 Starting email verification for:', email)
        console.log('🔑 Token:', token.substring(0, 10) + '...')
        
        // Make sure we're still in the verifying state
        setStatus('verifying')
        
        const response = await apiClient.verifyEmail(token, email)
        console.log('✅ Verification successful:', response)
        
        setStatus('success')
        setMessage(response.message || 'Email verified successfully!')
        
        // Refresh user data to get updated emailVerified status
        console.log('🔄 Refreshing user data...')
        await refreshUser()
        console.log('✅ User data refreshed')
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard')
        }, 3000)
      } catch (error) {
        console.error('❌ Verification failed:', error)
        setStatus('error')
        setMessage(error instanceof Error ? error.message : 'Failed to verify email')
      }
    }

    // Start verification immediately without delay to prevent double-renders
    verifyEmail()
  }, [token, email, refreshUser, navigate])

  const handleResendVerification = async () => {
    if (!email) {
      setResendMessage('Email address not available for resending verification')
      return
    }

    setResendLoading(true)
    setResendMessage('')
    
    try {
      const response = await apiClient.resendEmailVerification(email)
      setResendMessage(response.message || 'Verification email sent!')
    } catch (error) {
      setResendMessage(error instanceof Error ? error.message : 'Failed to resend verification email')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-firedup-dark flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">
            Email Verification
          </h2>
          <div className="w-16 h-1 bg-firedup-orange mx-auto mb-8"></div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-firedup-orange mx-auto mb-4"></div>
              <p className="text-gray-600">Verifying your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Verified!</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to dashboard in 3 seconds...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h3>
              <p className="text-gray-600 mb-6">{message}</p>
              
              {user && !user.emailVerified && (
                <div className="space-y-4">
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full bg-firedup-orange text-white py-2 px-4 rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                  
                  {resendMessage && (
                    <p className="text-sm text-gray-600">{resendMessage}</p>
                  )}
                </div>
              )}
              
              <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <p className="text-xs text-gray-500">
                  If you continue to have issues, the verification link may have expired or been used already.
                </p>
              </div>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h3>
              <p className="text-gray-600 mb-6">{message}</p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/" 
              className="text-firedup-orange hover:text-orange-600 text-sm font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
